import { motion } from 'framer-motion';
import Card from '../components/Card';
import cvData from '../data/cvData';

// Bölümler için ortak animasyon
const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: "easeOut" } 
  }
};

export default function CV() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-5xl md:text-6xl font-serif font-bold text-navy mb-4 tracking-tight"
          >
            Özgeçmiş
          </motion.h1>
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: "6rem" }}
            transition={{ duration: 0.8 }}
            className="h-1 bg-gradient-to-r from-transparent via-navy to-transparent mx-auto mb-4"
          ></motion.div>
        </div>

        {/* Personal Information */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="bg-white rounded-xl border-2 border-gray-200 p-8 mb-8 hover:shadow-xl transition-shadow duration-300"
        >
          <h2 className="text-2xl font-serif font-bold text-navy mb-6 pb-3 border-b border-gray-200">Kişisel Bilgiler</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
             {/* ... İçeriklerin aynısı ... */}
             {/* Kod tekrarı olmasın diye iç kısımları aynı bırakıyorum, sadece Wrapper'ı motion.div yaptık */}
             <div className="flex items-start gap-3"><span className="font-semibold text-navy min-w-[100px]">Ad:</span><span>{cvData.personal.name}</span></div>
             <div className="flex items-start gap-3"><span className="font-semibold text-navy min-w-[100px]">Ünvan:</span><span>{cvData.personal.title}</span></div>
             <div className="flex items-start gap-3"><span className="font-semibold text-navy min-w-[100px]">Üniversite:</span><span>{cvData.personal.university}</span></div>
             <div className="flex items-start gap-3"><span className="font-semibold text-navy min-w-[100px]">E-posta:</span><a href={`mailto:${cvData.personal.email}`} className="text-navy hover:underline">{cvData.personal.email}</a></div>
             <div className="flex items-start gap-3"><span className="font-semibold text-navy min-w-[100px]">Telefon:</span><span>{cvData.personal.phone}</span></div>
             <div className="flex items-start gap-3 md:col-span-2"><span className="font-semibold text-navy min-w-[100px]">Adres:</span><span>{cvData.personal.address}</span></div>
          </div>
        </motion.div>

        {/* Education */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="bg-white rounded-xl border-2 border-gray-200 p-8 mb-8 hover:shadow-xl transition-shadow duration-300"
        >
          <h2 className="text-2xl font-serif font-bold text-navy mb-6 pb-3 border-b border-gray-200">Eğitim</h2>
          <div className="space-y-8">
            {cvData.education.map((edu, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border-l-4 border-navy pl-6 relative"
              >
                <div className="absolute -left-2 top-0 w-4 h-4 bg-navy rounded-full border-2 border-white"></div>
                <h3 className="text-xl font-semibold text-navy mb-1">{edu.degree}</h3>
                <p className="text-gray-700 font-medium mb-1">{edu.field}</p>
                <p className="text-gray-600 mb-1">{edu.university}</p>
                <p className="text-gray-500 text-sm mb-2">{edu.year}</p>
                {edu.thesis && (
                  <p className="text-gray-600 mt-3 italic pl-4 border-l-2 border-gray-200">Tez: {edu.thesis}</p>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Research Interests */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-xl border-2 border-gray-200 p-8 mb-8 hover:shadow-xl transition-shadow duration-300"
        >
          <h2 className="text-2xl font-serif font-bold text-navy mb-6 pb-3 border-b border-gray-200">Araştırma İlgi Alanları</h2>
          <div className="flex flex-wrap gap-3">
            {cvData.researchInterests.map((interest, index) => (
              <motion.span
                key={index}
                whileHover={{ scale: 1.05 }}
                className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 font-medium text-sm hover:border-navy hover:bg-navy hover:text-white transition-colors duration-300"
              >
                {interest}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Experience - Similar structure to Education */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-xl border-2 border-gray-200 p-8 mb-8 hover:shadow-xl transition-shadow duration-300"
        >
          <h2 className="text-2xl font-serif font-bold text-navy mb-6 pb-3 border-b border-gray-200">İş Deneyimi</h2>
          <div className="space-y-8">
            {cvData.experience.map((exp, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border-l-4 border-navy-light pl-6 relative"
              >
                <div className="absolute -left-2 top-0 w-4 h-4 bg-navy-light rounded-full border-2 border-white"></div>
                <h3 className="text-xl font-semibold text-navy mb-1">{exp.position}</h3>
                <p className="text-gray-700 font-medium mb-1">{exp.institution}</p>
                <p className="text-gray-600 mb-1">{exp.department}</p>
                <p className="text-gray-500 text-sm">{exp.period}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Administrative Duties */}
        {cvData.administrativeDuties && cvData.administrativeDuties.length > 0 && (
          <motion.div 
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-white rounded-xl border-2 border-gray-200 p-8 mb-8 hover:shadow-xl transition-shadow duration-300"
          >
            <h2 className="text-2xl font-serif font-bold text-navy mb-6 pb-3 border-b border-gray-200">İdari Görevler</h2>
            <div className="space-y-8">
              {cvData.administrativeDuties.map((duty, index) => (
                <div key={index} className="border-l-4 border-navy-light pl-6 relative">
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-navy-light rounded-full border-2 border-white"></div>
                  <h3 className="text-xl font-semibold text-navy mb-1">{duty.position}</h3>
                  <p className="text-gray-700 font-medium mb-1">{duty.institution}</p>
                  <p className="text-gray-500 text-sm">{duty.period}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Languages */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-xl border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow duration-300"
        >
          <h2 className="text-2xl font-serif font-bold text-navy mb-6 pb-3 border-b border-gray-200">Diller</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
            {cvData.languages.map((lang, index) => (
              <motion.div 
                key={index} 
                whileHover={{ x: 5 }}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="font-semibold text-navy">{lang.name}</span>
                <span className="text-gray-600">{lang.level}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}